import React from 'react';
import PropTypes from 'prop-types';
import Loader from 'relements/build/esm/decorators/loader';
import ContextMenu from 'relements/build/esm/overlays/contextMenu';

import ModalV2 from '@src/components/ModalV2';
import EmptyCard from '@src/components/EmptyCard';
import CopyPaste from '@src/components/CopyPaste';

import { getAuditHeaders } from '@src/utils/AuditLogs';
import connection from '@src/connection';
import hashHistory from '@src/History';
import SmallTalkAPI from '@src/api/SmallTalkAPI';
import { SCREEN_TYPES, AlertTypes } from '@src/constants/MogamboConstants';
import { isNewPipeline, userHasPermission, CASName } from '@src/actions/_utils';

import BotStore from '@src/store';

import ListItem from './components/ListItem';
import CreateSkillset from './components/CreateSkillset';

import SkillsetsIcon from './icons/Skillsets.svg';
import FAQ from './icons/FAQ.svg';
import Smalltalk from './icons/Smalltalk.svg';

// Utils

import * as maps from './maps';

import { isProduction } from '../../utils';
import { PERMISSION_LEVELS, MIX_PANEL } from '../../const';

import styles from './Skillsets.scss';

// TODO: Optimize this flow by removing decorators and creating a functional component.
@connection(maps.listenToStores, maps.getStateFromStores)
@Loader({})
class Skillsets extends React.Component {
  constructor() {
    super();
    this.state = {
      smallTalkDisabled: false,
      newSkill: '',
      createSkillsetModalActive: false,
    };
  }

  componentDidMount() {
    this.props.activateLoader();
    maps.getAllStories(this.props.match.params.botId, () => {
      this.props.deactivateLoader();
      maps.setCurrentScreen(SCREEN_TYPES.STORIES);
    });

    this.getSmallTalkData(this.props.domain);
  }

  scrollSmoothHandler = () => {
    this.state.newSkill &&
      document.getElementById(this.state.newSkill).scrollIntoView({
        behavior: 'smooth',
        block: 'end',
      });
  };

  render() {
    const skillsetData = this.generateSkillsetData();
    const hasSelfServeLayout = BotStore.getSelfServeLayout();

    return (
      <div className={styles.Skillsets}>
        <div
          className={`${styles.SkillsetsHeader} ${
            hasSelfServeLayout ? styles.SkillsetsHeader_ww : ''
          }`}
        >
          <div className={styles.SkillsetsHeaderTitle}>Skill Sets</div>
          <div className={styles.SkillsetsHeaderSubtitle}>
            BUILD YOUR INTELLIGENT VIRTUAL ASSISTANT USING SKILL SETS, FAQs AND
            SMALL TALK
          </div>
        </div>

        <div
          className={`${styles.SkillsetsWrapper} ${
            hasSelfServeLayout ? styles.SkillsetsWrapper_ww : ''
          }`}
        >
          <div className={styles.SkillsetsContainer}>
            {skillsetData.length > 0 ? (
              <div
                className={`${styles.SkillsetsContainerList} ${
                  hasSelfServeLayout ? styles.SkillsetsContainerList_ww : ''
                }`}
              >
                <ListItem.Header
                  icon={SkillsetsIcon}
                  title="Skill Sets"
                  subText={
                    <span>
                      A Skill Set is a collection of skills,
                      <a
                        className={styles.SkillsetsEmptyCardLink}
                        target="_blank"
                        href="https://docs.haptik.ai/essentials/all-about-skill-set"
                        rel="noreferrer"
                      >
                        {' '}
                        learn more.
                      </a>
                    </span>
                  }
                  buttonText="Create Skill Set"
                  buttonClick={this.handleCreateSkillset}
                />
                <div
                  className={`${styles.SkillsetsContainerListData} ${
                    hasSelfServeLayout
                      ? styles.SkillsetsContainerListData_ww
                      : ''
                  }`}
                >
                  <div className={styles.SkillsetsContainerListDataTitle}>
                    Your Skill Sets
                  </div>
                  {this.renderSkillSetList(skillsetData)}
                </div>
              </div>
            ) : (
              <EmptyCard
                icon={SkillsetsIcon}
                title="Skill Sets"
                subtext={
                  <span>
                    A Skill Set is a collection of Skills that enable your IVA
                    to handle queries that need a complex conversational
                    journey.
                    <a
                      className={styles.SkillsetsEmptyCardLink}
                      target="_blank"
                      href="https://docs.haptik.ai/essentials/all-about-skill-set"
                      rel="noreferrer"
                    >
                      {' '}
                      Learn more
                    </a>
                  </span>
                }
                buttonText="Create Skill Set"
                onButtonClick={this.handleCreateSkillset}
              />
            )}

            <div
              className={`${styles.SkillsetsContainerRHS} ${
                hasSelfServeLayout ? styles.SkillsetsContainerRHS_ww : ''
              }`}
            >
              <EmptyCard
                icon={FAQ}
                title="FAQs"
                subtext={
                  <span>
                    FAQs enable your IVA to handle and respond to the common
                    queries that don&apos;t need a back and forth conversation.
                    <a
                      href="https://docs.haptik.ai/essentials/how-to-configure-faq-section"
                      className={styles.SkillsetsEmptyCardLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {' '}
                      Learn more
                    </a>
                  </span>
                }
                buttonText="Configure FAQs"
                onButtonClick={this.navToBotGeneralNodes}
              />

              <EmptyCard
                icon={Smalltalk}
                title="Small Talk"
                contextMenuOptions={this.renderSmallTalkContextMenuOptions({
                  active: !this.state.smallTalkDisabled,
                  id: 'SMALLTALK',
                })}
                subtext={
                  <span>
                    Small Talk enables your Virtual Assistant to have informal
                    conversations like &quot;What is the weather like&quot;,
                    &quot;Tell me a joke&quot;.
                    <a
                      className={styles.SkillsetsEmptyCardLink}
                      target="_blank"
                      href="https://docs.haptik.ai/essentials/how-to-configure-small-talk-section"
                      rel="noreferrer"
                    >
                      {' '}
                      Learn more
                    </a>
                  </span>
                }
                buttonText="Configure Small Talk"
                onButtonClick={this.navToSmallTalk}
              />
            </div>
          </div>
        </div>

        <ModalV2
          title="Create New Skill Set"
          active={this.state.createSkillsetModalActive}
          classPrefix="modalV2"
          isV2
          toggleModal={() => {
            this.setState({
              createSkillsetModalActive: false,
            });
          }}
        >
          <CreateSkillset
            onCreate={this.createSkillset}
            skillsetData={skillsetData}
          />
        </ModalV2>
      </div>
    );
  }

  renderSkillSetList = (skillSetData) => {
    const retData = [];
    let count = 0;

    skillSetData.forEach((skillset) => {
      count++;
      const skillId = skillset.columns[0].id;
      const highlightSkill =
        this.state.newSkill && skillId === this.state.newSkill;

      retData.push(
        <ListItem
          id={skillId}
          key={`skillset-${skillId}`}
          title={skillset.columns[0].title}
          contextMenuOptions={this.renderContextMenuOptions(
            skillset.columns[0]
          )}
          lastContextMenuItem={
            skillSetData.length > 20 && count === skillSetData.length
          }
          handleOnClick={() => this.navToSubstory(skillset)}
          disabled={!skillset.columns[0].active}
          highlight={highlightSkill}
        />
      );
    });

    setTimeout(() => {
      this.scrollSmoothHandler();
    }, 500);

    return retData;
  };

  generateSkillsetData = () => {
    const { allStories, match } = this.props;
    const { botId } = match.params;

    const allSkills = allStories
      .sort((a, b) => b.active - a.active)
      .map((story) => {
        const row = {
          disabled: !story.active,
          botId,
          id: story.id,
          columns: [],
        };
        row.columns.push(story);

        return row;
      });

    return allSkills;
  };

  renderContextMenuOptions = (story) => (closeContextMenu) => {
    const allowCopy = !isNewPipeline(this.props.meta.domain);
    return (
      <>
        <ContextMenu.Item
          onClick={(e) => this.toggleSkillset(e, closeContextMenu, story)}
          iconType={story.active ? 'inactive' : 'success_response'}
          iconSize={18}
          title={story.active ? 'Deactivate' : 'Activate'}
          className={
            story.active ? styles.SkillsetsDeactivate : styles.SkillsetsActivate
          }
        />
        {allowCopy ? (
          <ContextMenu.Item
            onClick={(e) =>
              this.handleCopySkillset(story.title, e, closeContextMenu)
            }
            iconType="synonym_duplicate"
            iconSize={18}
            title="Copy"
          />
        ) : null}
      </>
    );
  };

  navToSubstory = (story) => {
    hashHistory.push(
      `/v2/bots/${this.props.match.params.botId}/stories/${story.id}/substories`
    );
  };

  handleCreateSkillset = () => {
    this.setState({
      newSkill: '',
      createSkillsetModalActive: true,
    });
  };

  handleCopySkillset = (title, e, closeContextMenu) => {
    e.stopPropagation();
    closeContextMenu(e);
    maps.openComponentModal(
      <CopyPaste
        copyType="story"
        name={title}
        sourceDomain={(this.props.domain && this.props.domain.title) || ''}
      />,
      'Copy Story',
      null,
      'close'
    );
  };

  createSkillset = (skillsetName) => {
    maps.createNewStory(
      skillsetName,
      this.props.domain.id,
      this.props.domain.title,
      (response) => {
        this.setState({
          newSkill: response.story.id,
        });

        const mixPanelTracker = BotStore.getMixPanel();
        mixPanelTracker.track({
          event: MIX_PANEL.SMART_SKILLS.CREATE_SKILLSET,
          payload: {
            'Bot Name': this.props.domain.title,
            'Skillset name': skillsetName,
          },
        });
      }
    );

    this.setState({
      createSkillsetModalActive: false,
    });
  };

  navToBotGeneralNodes = () => {
    hashHistory.push(`/bot/${this.props.match.params.botId}/general-nodes`);

    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.SMART_SKILLS.CONFIGURE_FAQ,
      payload: { 'Bot Name': this.props.domain.title },
    });
  };

  navToSmallTalk = () => {
    hashHistory.push(`/v2/bots/${this.props.match.params.botId}/smalltalk`);

    const mixPanelTracker = BotStore.getMixPanel();
    mixPanelTracker.track({
      event: MIX_PANEL.SMART_SKILLS.CONFIGURE_SMALLTALK,
      payload: { 'Bot Name': this.props.domain.title },
    });
  };

  renderSmallTalkContextMenuOptions = (smalltalk) => (closeContextMenu) => {
    return (
      <ContextMenu.Item
        onClick={(e) => {
          e.stopPropagation();
          closeContextMenu(e);

          if (smalltalk.active) this.disableSmallTalk();
          else this.enableSmallTalk();
        }}
        iconType={smalltalk.active ? 'inactive' : 'success_response'}
        iconSize={18}
        title={smalltalk.active ? 'Deactivate' : 'Activate'}
        className={
          smalltalk.active
            ? styles.SkillsetsDeactivate
            : styles.SkillsetsActivate
        }
      />
    );
  };

  getSmallTalkData = (domain) => {
    if (!domain || !domain.title) return;
    this.SmallTalkAPI = new SmallTalkAPI(domain.title);
    this.SmallTalkAPI.getDetails()
      .then((response) => {
        const { are_all_intents_disabled: smallTalkDisabled } = response.body;
        this.setState({ smallTalkDisabled });
      })
      .catch(() => {
        maps.createAlert(
          'Error fetching smalltalk details',
          null,
          AlertTypes.NEGATIVE
        );
      });
  };

  enableSmallTalk = () => {
    const mixPanelTracker = BotStore.getMixPanel();
    if (!this.isEditable())
      return maps.createAlert(
        'You do not have permissions to do this',
        null,
        AlertTypes.NEGATIVE
      );

    this.props.activateLoader();
    this.SmallTalkAPI.enableAllIntents()
      .then(() => {
        mixPanelTracker.track({
          event: MIX_PANEL.SMART_SKILLS.SMALLTALK_ACTIVATED,
          payload: { CAS: CASName(), 'Bot Name': this.props.domain.title },
        });
        this.setState({ smallTalkDisabled: false });
      })
      .catch(() => {
        maps.createAlert('Error enabling smalltalk', null, AlertTypes.NEGATIVE);
      })
      .finally(this.props.deactivateLoader);
  };

  disableSmallTalk = () => {
    const mixPanelTracker = BotStore.getMixPanel();
    if (!this.isEditable())
      return maps.createAlert(
        'You do not have permissions to do this',
        null,
        AlertTypes.NEGATIVE
      );

    this.props.activateLoader();
    this.SmallTalkAPI.disableAllIntents()
      .then(() => {
        mixPanelTracker.track({
          event: MIX_PANEL.SMART_SKILLS.SMALLTALK_DEACTIVATED,
          payload: { CAS: CASName(), 'Bot Name': this.props.domain.title },
        });
        this.setState({ smallTalkDisabled: true });
      })
      .catch(() => {
        maps.createAlert(
          'Error disabling smalltalk',
          null,
          AlertTypes.NEGATIVE
        );
      })
      .finally(this.props.deactivateLoader);
  };

  isEditable() {
    return (
      !isProduction() && userHasPermission(PERMISSION_LEVELS.MOGAMBO_ALL_EDIT)
    );
  }

  /**
   * Toggles a skillset active/inactive
   * @param {Object} e target event
   * @param {Function} closeContextMenu function to close context menu
   * @param {Object} skillset skillset object to toggle
   */
  toggleSkillset = (e, closeContextMenu, skillset) => {
    e.stopPropagation();
    closeContextMenu(e);

    const mixPanelTracker = BotStore.getMixPanel();
    const headers = getAuditHeaders({
      domain: this.props.meta.domain,
      eventName: 'story_update',
    });

    const mixPanelPayload = {
      'Bot Name': this.props.domain.title,
      'Skillset name': skillset.title,
    };

    if (skillset.active)
      maps.makeStoryInactive(skillset.id, headers, () => {
        mixPanelTracker.track({
          event: MIX_PANEL.SMART_SKILLS.SKILLSET_DEACTIVATED,
          payload: { ...mixPanelPayload },
        });
      });
    else
      maps.makeStoryActive(skillset.id, headers, () => {
        mixPanelTracker.track({
          event: MIX_PANEL.SMART_SKILLS.SKILLSET_ACTIVATED,
          payload: { ...mixPanelPayload },
        });
      });
  };
}

Skillsets.propTypes = {
  botId: PropTypes.string,
  activateLoader: PropTypes.func,
  deactivateLoader: PropTypes.func,
  allStories: PropTypes.arrayOf(PropTypes.object),
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  match: PropTypes.shape({
    params: PropTypes.shape({
      botId: PropTypes.string,
    }),
  }),
  meta: PropTypes.shape({
    domain: PropTypes.shape(),
  }),
};

Skillsets.defaultProps = {
  botId: '',
  allStories: [],
  domain: {},
  meta: null,
  match: null,
  activateLoader: () => null,
  deactivateLoader: () => null,
};

export default Skillsets;
